import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Badge,
  Alert,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { updateTask, getTask } from "../api/tasks";
import { getMembers } from "../api/api";
import toast from "../utils/toastUtils";

const TaskEditModal = ({ show, onHide, task, onTaskUpdated }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    dueDate: "",
    assignedTo: "",
    projectId: "",
    relationships: {
      project: null,
      committee: null,
      meeting: null,
    },
    labels: [],
    createdBy: null,
  });

  const [newLabel, setNewLabel] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initialize form data from task
  useEffect(() => {
    if (task) {
      // Determine relationships from either relatedTo or relationships field
      const relationships = {
        project: null,
        committee: null,
        meeting: null,
      };

      // First check relatedTo field
      if (task.relatedTo?.type && task.relatedTo?.id && task.relatedTo?.name) {
        relationships[task.relatedTo.type] = {
          id: task.relatedTo.id,
          name: task.relatedTo.name,
          type: task.relatedTo.type,
        };
      }
      // Then check relationships field (if different from relatedTo)
      else if (
        task.relationships?.type &&
        task.relationships?.id &&
        task.relationships?.name
      ) {
        relationships[task.relationships.type] = {
          id: task.relationships.id,
          name: task.relationships.name,
          type: task.relationships.type,
        };
      }
      // Also handle projectId if it exists
      else if (task.projectId) {
        const project = availableProjects.find((p) => p._id === task.projectId);
        if (project) {
          relationships.project = {
            id: task.projectId,
            name: project.title,
            type: "project",
          };
        }
      }

      setFormData({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "To Do",
        priority: task.priority || "Medium",
        dueDate: task.dueDate?.split("T")[0] || "",
        assignedTo: task.assignedTo?._id || "",
        projectId: task.projectId || "",
        relationships: relationships,
        labels: task.labels || [],
        createdBy: task.createdBy,
      });
    }
  }, [task, availableProjects]);

  // Fetch users and projects for dropdowns
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch users for assignee dropdown
        const usersData = await getMembers();
        setUsers(usersData || []);

        // For projects, ideally we'd call a getProjects API
        // This is a placeholder - replace with actual API call
        // const projectsData = await getProjects();
        // setAvailableProjects(projectsData || []);
        setAvailableProjects([]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setErrorMessage("Failed to load users or projects");
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      fetchData();
    }
  }, [show]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Add label to task
  const handleAddLabel = () => {
    if (newLabel.trim() && !formData.labels.includes(newLabel.trim())) {
      setFormData({
        ...formData,
        labels: [...formData.labels, newLabel.trim()],
      });
      setNewLabel("");
    }
  };

  // Remove label from task
  const handleRemoveLabel = (labelToRemove) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter((label) => label !== labelToRemove),
    });
  };

  // Submit task update
  const handleSubmitTask = async () => {
    if (!formData.title) {
      setErrorMessage("Title is required");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      // Prepare task data for update
      const taskData = {
        title: formData.title,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        dueDate: formData.dueDate,
        assignedTo: formData.assignedTo || null,
        labels: formData.labels,
      };

      // Handle relationships - Find the first non-null relationship
      const activeRelationship = Object.entries(formData.relationships).find(
        ([_, data]) => data !== null
      );

      if (activeRelationship) {
        const [type, data] = activeRelationship;

        // Ensure data has all required fields
        if (data && data.id && data.name && data.type) {
          // Set the relatedTo field for server-side
          taskData.relatedTo = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // Also set the relationships field for client-side
          taskData.relationships = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // For backwards compatibility with projectId
          if (data.type === "project") {
            taskData.projectId = data.id;
          }
        }
      }

      // Update the task
      await updateTask(task._id, taskData);

      // Get the updated task to return to parent
      const updatedTask = await getTask(task._id);

      // Notify parent component
      if (onTaskUpdated) {
        onTaskUpdated(updatedTask);
      }

      // Close the modal
      onHide();
    } catch (error) {
      console.error("Error updating task:", error);
      setErrorMessage("Failed to update task");
      toast.error("Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
      >
        <Modal.Title style={{ color: "#d9669b" }}>
          <FontAwesomeIcon icon={faEdit} className="me-2" />
          Edit Task
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Task Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter task title"
              required
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #f5c2c2",
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter task description"
              style={{
                backgroundColor: "#fafafa",
                border: "1px solid #f5c2c2",
              }}
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                >
                  <option value="To Do">To Do</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Done">Done</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Assigned To</Form.Label>
                <Form.Select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                >
                  <option value="">Unassigned</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Project</Form.Label>
                <Form.Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                  disabled={availableProjects.length === 0}
                >
                  <option value="">None</option>
                  {availableProjects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Labels</Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Add a label"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddLabel();
                  }
                }}
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
              <Button
                variant="outline-primary"
                onClick={handleAddLabel}
                style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
              >
                Add
              </Button>
            </InputGroup>

            {formData.labels.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {formData.labels.map((label) => (
                  <Badge
                    key={label}
                    bg="light"
                    text="dark"
                    className="mb-1 d-flex align-items-center"
                    style={{ border: "1px solid #f5c2c2" }}
                  >
                    # {label}
                    <span
                      onClick={() => handleRemoveLabel(label)}
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                    >
                      &times;
                    </span>
                  </Badge>
                ))}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitTask}
          disabled={loading}
          style={{
            background: "linear-gradient(to right, #e66b8f, #d4af37)",
            border: "none",
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskEditModal;
